import 'react-loading-skeleton/dist/skeleton.css';

import {
  GENERAL_ENQUIRIES,
  MEDIA_PARTNERSHIP_EMAIL,
  SPEAKER_EMAIL,
  SPONSOR_EMAIL,
} from '@utils/constants';
import { Trans, useTranslation } from 'react-i18next';
import {
  AppLogo,
  CompanyLogo,
  ContactUs,
  DesktopOnlyColumn,
  DetailTitle,
  EventDetailsDescription,
  EventDetailsSection,
  FooterRoot,
  FooterWrapper,
  LinkElement,
  LinkElementRoot,
  MenuFooterRoot,
  MobileOnlyColumn,
  TextLink,
} from './styles';

import weLogo from '@assets/images/weCONECT_col_RGB.svg';
import Button from '@components/Button';
import { Column } from '@components/LinearLayout';
import { adminPortfoliosRouteFormat } from '@constants/paths';
import { getLanguage } from '@store/slices/configSlice';
import { getEventDetails } from '@store/slices/eventSlice';
import {
  ACCOUNT_ID,
  usePublicCoLocatedEventsByEventId,
  usePublicPortfolioByAccount,
} from '@utils/apiHooks';
import { getImageSrc } from '@utils/fn';
import { useMobileWidth, useTabletWidth } from '@utils/hooks';
import { eventTimeFormat } from '@utils/time';
import { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import SaveTheDate from '@composites/InboundButton/components/SaveTheDate';
import { MLSID } from '@utils/pageBuilderHelper';

export function FooterMenu() {
  const { t } = useTranslation();
  const language = useSelector(getLanguage);
  const theme = useTheme();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const isTablet = useTabletWidth();
  const isMobile = useMobileWidth();

  const { data, isLoading } = usePublicCoLocatedEventsByEventId({
    eventId: sluggedEvent?.id,
    enabled: sluggedEvent?.id?.length > 0 && sluggedEvent?.colocated,
  });

  const { data: portfolios } = usePublicPortfolioByAccount({
    accountId: ACCOUNT_ID,
  });

  const portfolioName = sluggedEvent?.portfolio?.name;

  const location = useMemo(
    () =>
      [
        sluggedEvent?.location?.name,
        sluggedEvent?.location?.marker.street,
        [
          sluggedEvent?.location?.marker?.postalCode,
          sluggedEvent?.location?.marker?.city,
        ]
          ?.filter(e => e)
          ?.join(' '),
      ].filter(e => e) ?? [],
    [
      sluggedEvent?.location?.marker?.city,
      sluggedEvent?.location?.marker?.postalCode,
      sluggedEvent?.location?.marker.street,
      sluggedEvent?.location?.name,
    ]
  );

  const eventElements = useMemo(
    () => [
      { title: t('sponsoring'), link: `mailto:${SPONSOR_EMAIL}` },
      { title: t('to-speaker'), link: `mailto:${SPEAKER_EMAIL}` },
      {
        title: t('media-partnership'),
        link: `mailto:${MEDIA_PARTNERSHIP_EMAIL}`,
      },
      { title: t('general-enquiries'), link: `mailto:${GENERAL_ENQUIRIES}` },
    ],
    [t]
  );

  const getLogoSrc = () => {
    const eventLogo = sluggedEvent?.design?.eventLogoPosSvg;
    const portfolioLogo = portfolios?.data?.find(
      p => p?.id === sluggedEvent?.portfolio?.id
    )?.design?.logoPosImage;

    if (portfolioLogo) {
      return getImageSrc(portfolioLogo);
    } else if (eventLogo) {
      return getImageSrc(eventLogo);
    } else {
      return weLogo;
    }
  };
  const getLogoAlt = () => {
    if (sluggedEvent?.portfolio?.name) {
      return sluggedEvent?.portfolio?.name;
    } else {
      return sluggedEvent?.name?.length ? sluggedEvent?.name : 'WeConect Logo';
    }
  };

  return (
    <MenuFooterRoot $backgroundColor={theme.color.lightGrey}>
      <FooterRoot>
        <FooterWrapper>
          <EventDetailsSection>
            <CompanyLogo src={getLogoSrc()} alt={getLogoAlt()} loading="lazy" />

            {portfolioName && (
              <EventDetailsDescription>
                {sluggedEvent?.name}
                <Trans
                  i18nKey="event-details-description"
                  portfolioName={sluggedEvent?.portfolio?.name}
                >
                  is part of the {{ portfolioName }} Event Series. More
                  information can be found
                  <TextLink
                    href={adminPortfoliosRouteFormat(
                      sluggedEvent?.portfolio?.slug,
                      ''
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    $color={sluggedEvent?.design?.themeColor}
                  >
                    here
                  </TextLink>
                  .
                </Trans>
              </EventDetailsDescription>
            )}
          </EventDetailsSection>
          <EventDetailsSection>
            <Column>
              <DetailTitle>{t('date')}</DetailTitle>
              {isLoading ? (
                <Skeleton
                  count={1}
                  width="20rem"
                  baseColor="#dedede"
                  enableAnimation
                />
              ) : (
                <EventDetailsDescription>
                  <time dateTime={sluggedEvent?.start_date}>
                    {eventTimeFormat(
                      sluggedEvent?.start_date,
                      sluggedEvent?.end_date,
                      language
                    )}
                  </time>
                </EventDetailsDescription>
              )}
            </Column>

            {location?.filter(e => e)?.length > 0 && (
              <Column>
                <DetailTitle>{t('location')}</DetailTitle>

                {isLoading ? (
                  <Skeleton
                    count={3}
                    width="15rem"
                    enableAnimation
                    baseColor="#dedede"
                    style={{ marginTop: '0.8rem' }}
                  />
                ) : (
                  <Column $gap="0.3rem">
                    {location?.map(item => (
                      <EventDetailsDescription key={item}>
                        {item}
                      </EventDetailsDescription>
                    ))}
                  </Column>
                )}
              </Column>
            )}

            <div>
              <DetailTitle $marginBottom={isMobile ? '0.5rem' : '3rem'}>
                {t('get-our-app')}
              </DetailTitle>
              <Column $gap="1rem">
                <LinkElementRoot to="https://apps.apple.com/bw/app/hubs101/id1585353517">
                  <LinkElement $color={sluggedEvent?.design?.themeColor}>
                    <AppLogo
                      src={
                        'https://media.we-conect.com/images/website/Apple_badge.png'
                      }
                    />
                  </LinkElement>
                </LinkElementRoot>

                <LinkElementRoot to="https://play.google.com/store/apps/details?id=com.liveware.hubs101">
                  <LinkElement $color={sluggedEvent?.design?.themeColor}>
                    <AppLogo
                      src={
                        'https://media.we-conect.com/images/website/Google_badge.png'
                      }
                    />
                  </LinkElement>
                </LinkElementRoot>
              </Column>
            </div>
          </EventDetailsSection>

          <EventDetailsSection>
            <DesktopOnlyColumn>
              <ContactUs href="mailto:contact@we-conect.com">
                <DetailTitle $color={sluggedEvent?.design?.themeColor}>
                  {t('contact-us')}
                </DetailTitle>
                <Column $gap="0.5rem">
                  <LinkElementRoot to="tel:+493052107030">
                    <LinkElement $color={sluggedEvent?.design?.themeColor}>
                      <span>{'+49 (0)30 52 10 70 3 0'}</span>
                    </LinkElement>
                  </LinkElementRoot>
                </Column>
              </ContactUs>

              <DetailTitle>{t('email')}</DetailTitle>
              <Column $gap="0.5rem">
                {eventElements?.map(item => (
                  <LinkElementRoot key={item.title} to={item.link}>
                    <LinkElement $color={sluggedEvent?.design?.themeColor}>
                      <span>&gt;</span> <span>{item.title}</span>
                    </LinkElement>
                  </LinkElementRoot>
                ))}
              </Column>
            </DesktopOnlyColumn>

            <MobileOnlyColumn>
              <DetailTitle $color={sluggedEvent?.design?.themeColor}>
                {t('phone')}
              </DetailTitle>
              <Column $gap="0.5rem" margin="0 0 0 12rem">
                <LinkElementRoot to="tel:+493052107030">
                  <LinkElement $color={sluggedEvent?.design?.themeColor}>
                    <span>{'+49 (0)30 52 10 70 3 0'}</span>
                  </LinkElement>
                </LinkElementRoot>
              </Column>

              <DetailTitle>{t('email')}</DetailTitle>
              <Column $gap="0.5rem" margin="0 0 0 12rem">
                {eventElements?.map(item => (
                  <LinkElementRoot key={item.title} to={item.link}>
                    <LinkElement $color={sluggedEvent?.design?.themeColor}>
                      <span>&gt;</span> <span>{item.title}</span>
                    </LinkElement>
                  </LinkElementRoot>
                ))}
              </Column>
            </MobileOnlyColumn>

            {sluggedEvent?.colocated && (
              <SaveTheDate
                label={t('save-the-date')}
                buttonType={MLSID.SAVE_THE_DATE}
                width={isMobile ? 'auto' : '100%'}
                padding={isMobile ? '1.5rem 4.5rem' : '1.1rem 0'}
              />
            )}
          </EventDetailsSection>

          {sluggedEvent?.colocated ? (
            <EventDetailsSection>
              <EventDetailsDescription>
                {t('this-event-is-co-located-with')}
              </EventDetailsDescription>
              {isLoading ? (
                <Column $gap="1.5rem">
                  <Skeleton
                    count={1}
                    enableAnimation
                    width="15rem"
                    height="10rem"
                    baseColor="#dedede"
                  />
                  <Skeleton
                    count={1}
                    enableAnimation
                    width="15rem"
                    height="10rem"
                    baseColor="#dedede"
                  />
                </Column>
              ) : (
                <Column $gap="2rem">
                  {data
                    ?.filter(e => e?.id !== sluggedEvent?.id)
                    ?.map(event => (
                      <CompanyLogo
                        key={event?.id}
                        src={getImageSrc(event?.images.logoTransparent)}
                      />
                    ))}
                </Column>
              )}
            </EventDetailsSection>
          ) : (
            <EventDetailsSection>
              <EventDetailsDescription>
                {t('add-event-to-calendar-text')}
              </EventDetailsDescription>

              <SaveTheDate
                label={t('save-the-date')}
                buttonType={MLSID.SAVE_THE_DATE}
                width={isMobile ? 'auto' : '100%'}
                padding={isMobile ? '1.5rem  4.5rem' : '1.1rem 0'}
              />
            </EventDetailsSection>
          )}
        </FooterWrapper>
      </FooterRoot>
    </MenuFooterRoot>
  );
}

export default FooterMenu;
